import { api, apiErrorMessage } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Field,
    Input,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { Tenant } from '@/api/types';
import { useWrite } from '@/lib/queries';

/** The tenant's `settings.onboarding` block, read defensively: the contract types `settings` as an opaque object. */
export function onboardingSettings(t: Pick<Tenant, 'settings'>): {
    expected_review: string;
    support_email: string;
    sandbox_url: string;
} {
    const settings =
        t.settings && !Array.isArray(t.settings)
            ? (t.settings as Record<string, unknown>)
            : {};
    const o =
        settings.onboarding && typeof settings.onboarding === 'object'
            ? (settings.onboarding as Record<string, unknown>)
            : {};
    const s = (v: unknown) => (typeof v === 'string' ? v : '');
    return {
        expected_review: s(o.expected_review),
        support_email: s(o.support_email),
        sandbox_url: s(o.sandbox_url),
    };
}

/**
 * BM-59: what the merchant reads after submitting (the expected review time and the onboarding support email) and
 * the sandbox the dashboard links to before approval. Saved on the tenant's settings with its version; empty
 * fields fall back to the platform defaults (and no sandbox link).
 */
export function OnboardingSection({ tenant }: { tenant: Tenant }) {
    const [form, setForm] = useState(() => onboardingSettings(tenant));
    const [error, setError] = useState<string | null>(null);
    const initial = onboardingSettings(tenant);
    const dirty =
        form.expected_review !== initial.expected_review ||
        form.support_email !== initial.support_email ||
        form.sandbox_url !== initial.sandbox_url;
    const save = useWrite(async () => {
        const settings =
            tenant.settings && !Array.isArray(tenant.settings)
                ? (tenant.settings as Record<string, unknown>)
                : {};
        return (
            await api().patch<Tenant>(`/staff/tenants/${tenant.id}`, {
                version: tenant.version,
                settings: { ...settings, onboarding: form },
            })
        ).data;
    }, ['/staff/tenants']);
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm font-medium">
                    Onboarding
                </CardTitle>
            </CardHeader>
            <CardContent>
                <form
                    method="post"
                    className="grid grid-cols-1 gap-3 md:grid-cols-3"
                    data-testid="tenant-onboarding"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setError(null);
                        save.mutate(undefined, {
                            onError: (err) => setError(apiErrorMessage(err)),
                        });
                    }}
                >
                    <Field
                        id="onboarding-expected-review"
                        label="Expected review time"
                        hint="Shown to the merchant after it submits. Empty: within 2 business days."
                    >
                        <Input
                            id="onboarding-expected-review"
                            value={form.expected_review}
                            maxLength={80}
                            placeholder="within 2 business days"
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    expected_review: e.target.value,
                                })
                            }
                        />
                    </Field>
                    <Field
                        id="onboarding-support-email"
                        label="Onboarding support email"
                        hint="The merchant's contact while its application is reviewed."
                    >
                        <Input
                            id="onboarding-support-email"
                            type="email"
                            value={form.support_email}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    support_email: e.target.value,
                                })
                            }
                        />
                    </Field>
                    <Field
                        id="onboarding-sandbox-url"
                        label="Sandbox URL"
                        hint="The dashboard links here before approval. Empty: no link."
                    >
                        <Input
                            id="onboarding-sandbox-url"
                            type="url"
                            value={form.sandbox_url}
                            placeholder="https://"
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    sandbox_url: e.target.value,
                                })
                            }
                        />
                    </Field>
                    <div className="flex items-center gap-3 md:col-span-3">
                        <Button
                            type="submit"
                            size="sm"
                            disabled={!dirty || save.isPending}
                        >
                            Save onboarding settings
                        </Button>
                        {error ? (
                            <p
                                role="alert"
                                className="text-destructive text-xs"
                            >
                                {error}
                            </p>
                        ) : null}
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
